import { Away, CharacterState, EntityKind, FOOT_HALF_WIDTH, FOOT_HEIGHT, NO_HANDLE, characterStateOf, mayHarm, type EntityState } from "odyssey-shared";

/**
 * The page's target (P2.2-T3, the Phase 2 spec's question 4, ADR-034's amendment of 2026-10-01): a left click on a
 * character's, a creature's or a training dummy's body as drawn selects it, Tab selects the nearest the player may
 * attack and each press after the next nearest (Shift+Tab the other way), and Escape clears it. The page sends each change as a `target` and holds it
 * at once; the map server judges it against the same view, so a target the page may choose is one it holds too. The
 * selection is dropped, with nothing sent, when the snapshot no longer holds the target or holds it dead, as the map
 * server drops it in the same tick. The page decides nothing about a fight here: who may be attacked only orders
 * Tab's choices, from the flag bytes the snapshot carries, and the map server judges every act.
 */

/**
 * The window the page counts its own `target` frames over, in ms: the map server admits `MAX_TARGETS_PER_SECOND` a
 * second and drops the rest, so the page keeps ten to a longer span, as it does its acts (`ACT_WINDOW_MS`).
 */
export const TARGET_WINDOW_MS = 1200;

/**
 * Most `target` frames the page sends in `TARGET_WINDOW_MS`.
 */
const TARGETS_PER_WINDOW = 10;

/**
 * A point the world writes into: a feet point, and, for a target, the top of its drawn body above it.
 */
export interface FeetPoint {
  x: number;
  y: number;
  top: number;
}

/**
 * What the targeting reads of the world the page draws: the client's view, the page's own handle, and where feet
 * stand as drawn, in sixteenths.
 */
export interface TargetWorld {
  /** The client's view: every entity the last snapshot holds. */
  readonly entities: ReadonlyMap<number, EntityState>;
  /** The page's own handle, -1 outside a session. */
  readonly own: number;
  /**
   * Where the page's own feet stand now, predicted.
   *
   * @param out Where the point is written, in sixteenths.
   * @returns False when no character is drawn.
   */
  ownFeet(out: FeetPoint): boolean;
  /**
   * Where a handle's feet box stands as drawn, and the top of its drawn body: a character where its sprite stands this
   * frame, its head's height above, a training dummy at the base of its art, which the map server's blows meet
   * (ADR-058's amendment, item 4), the top of its art above.
   *
   * @param handle The handle.
   * @param out Where the point and the top are written, in sixteenths.
   * @returns False when the page draws nothing at the handle.
   */
  feet(handle: number, out: FeetPoint): boolean;
}

/**
 * Whether a state the snapshot holds may be a target: a character that is not dead, a creature or a training dummy.
 *
 * @param state The entity's state.
 * @returns True when it may.
 */
export function targetable(state: Readonly<EntityState>): boolean {
  if (state.kind === EntityKind.Player) {
    return characterStateOf(state.anim) !== CharacterState.Dead;
  }
  return state.kind === EntityKind.Creature || state.kind === EntityKind.Dummy;
}

/**
 * Whether the player may attack a target, as far as the page can tell from the flag bytes (ADR-073 decision 6): a
 * training dummy always, a creature always, and a character when `mayHarm` says so with neither standing on a rule
 * bit, since a Sanctuary already writes Protected into the byte of anyone it shelters, and neither frozen by a Game
 * Master, which the away state says (`Away.Held`; ADR-056's amendment "a frozen character is safe", decision 45): a
 * held character neither harms nor is harmed. It orders Tab's choices and colours the frame's bar; the map server
 * decides. The page holds no party and no truce (nothing of either is sent until the party's page, P2.13-T5), so it
 * passes the truce as false on purpose: a hint that greys what cannot land, never a decision.
 *
 * @param own The player's own state, undefined before its entity arrives.
 * @param state The target's state.
 * @returns True when the page reads the target as attackable.
 */
export function attackable(own: Readonly<EntityState> | undefined, state: Readonly<EntityState>): boolean {
  if (state.kind !== EntityKind.Player) {
    return true;
  }
  return own !== undefined && mayHarm({ kind: "character", flag: own.pvp, zone: 0, duel: 0, held: own.away === Away.Held, party: 0, preference: 0 }, { kind: "character", flag: state.pvp, zone: 0, duel: 0, held: state.away === Away.Held, party: 0, preference: 0 }, false);
}

const SPOT: FeetPoint = { x: 0, y: 0, top: 0 };
const OWN: FeetPoint = { x: 0, y: 0, top: 0 };

/**
 * The target under a point: the entity, other than the page's own, whose body as drawn holds the point, edges
 * included: its feet box's width, from its feet up to the top of its drawn body (a feet box alone is too small to
 * click); where bodies overlap, the one standing lowest on screen, the one drawn in front, then the lower handle.
 *
 * @param world The world as drawn.
 * @param x The point's x in sixteenths.
 * @param y The point's y in sixteenths.
 * @returns The handle, or `NO_HANDLE` when no box holds the point.
 */
export function targetAt(world: TargetWorld, x: number, y: number): number {
  let found = NO_HANDLE;
  let foundY = Number.NEGATIVE_INFINITY;
  for (const state of world.entities.values()) {
    const handle = state.handle;
    if (handle === world.own || !targetable(state) || !world.feet(handle, SPOT)) {
      continue;
    }
    if (x < SPOT.x - FOOT_HALF_WIDTH || x > SPOT.x + FOOT_HALF_WIDTH || y < Math.min(SPOT.top, SPOT.y - FOOT_HEIGHT) || y > SPOT.y) {
      continue;
    }
    if (SPOT.y > foundY || (SPOT.y === foundY && handle < found)) {
      found = handle;
      foundY = SPOT.y;
    }
  }
  return found;
}

/**
 * Tab's choice: the targets the player may attack, nearest first by the squared distance from the page's own feet to
 * each one's feet as drawn, ties to the lower handle; forward (Tab), the one after the current target in that order,
 * the nearest after the farthest, or the nearest when the current target is none of them; backward (Shift+Tab), the
 * one before it, the farthest before the nearest, or the farthest when it is none of them.
 *
 * @param world The world as drawn.
 * @param current The current target, `NO_HANDLE` for none.
 * @param step 1 forward, -1 backward.
 * @returns The handle chosen, or `NO_HANDLE` when nothing may be attacked.
 */
export function nextTarget(world: TargetWorld, current: number, step: 1 | -1 = 1): number {
  if (!world.ownFeet(OWN)) {
    return NO_HANDLE;
  }
  const own = world.entities.get(world.own);
  const ranked: { readonly handle: number; readonly distance: number }[] = [];
  for (const state of world.entities.values()) {
    const handle = state.handle;
    if (handle === world.own || !targetable(state) || !attackable(own, state) || !world.feet(handle, SPOT)) {
      continue;
    }
    const dx = SPOT.x - OWN.x;
    const dy = SPOT.y - OWN.y;
    ranked.push({ handle, distance: dx * dx + dy * dy });
  }
  if (ranked.length === 0) {
    return NO_HANDLE;
  }
  ranked.sort((a, b) => a.distance - b.distance || a.handle - b.handle);
  const at = ranked.findIndex((entry) => entry.handle === current);
  const count = ranked.length;
  const next = at < 0 ? (step > 0 ? 0 : count - 1) : (at + step + count) % count;
  return (ranked[next] as { readonly handle: number }).handle;
}

/**
 * What the targeting asks of the session: a way to send a `target`.
 */
export interface TargetingHost {
  /**
   * Sends a `target`.
   *
   * @param handle The handle, `NO_HANDLE` to clear.
   * @returns False when the connection could not send it.
   */
  readonly send: (handle: number) => boolean;
}

/**
 * The page's selection.
 */
export class Targeting {
  private current = NO_HANDLE;
  private readonly sentAt = new Array<number>(TARGETS_PER_WINDOW).fill(Number.NEGATIVE_INFINITY);
  private sentNext = 0;

  /**
   * @param host The connection's send.
   */
  public constructor(private readonly host: TargetingHost) {}

  /**
   * The selected handle.
   *
   * @returns The handle, `NO_HANDLE` for none.
   */
  public get handle(): number {
    return this.current;
  }

  /**
   * Selects a handle, or clears the selection with `NO_HANDLE`, and sends the change; nothing changes when the handle
   * is already selected, when the page has sent its ten for the window, or when the connection cannot send, so the
   * page never holds a selection the map server was not asked for.
   *
   * @param handle The handle, `NO_HANDLE` to clear.
   * @param nowMs The page clock in ms.
   * @returns True when the selection changed.
   */
  public select(handle: number, nowMs: number): boolean {
    if (handle === this.current || nowMs - (this.sentAt[this.sentNext] as number) < TARGET_WINDOW_MS || !this.host.send(handle)) {
      return false;
    }
    this.sentAt[this.sentNext] = nowMs;
    this.sentNext = (this.sentNext + 1) % TARGETS_PER_WINDOW;
    this.current = handle;
    return true;
  }

  /**
   * Drops the selection, sending nothing, when the view no longer holds it or holds it as something that may not be a
   * target (a character lying dead), as the map server drops it in the same tick; read after each applied snapshot.
   *
   * It is dropped too while the page's own character reads held: a hold clears the map server's target for the
   * character and tells nobody, and the map server takes no `target` from a held character, in the hold or in its
   * grace, so a selection kept or made then is one the map server does not hold (ADR-056's amendment "a frozen
   * character is safe", decision 42).
   *
   * @param entities The client's view.
   * @param ownHeld Whether the page's own character reads held in that view.
   * @returns True when the selection was dropped.
   */
  public follow(entities: ReadonlyMap<number, EntityState>, ownHeld = false): boolean {
    if (this.current === NO_HANDLE) {
      return false;
    }
    const state = entities.get(this.current);
    if (!ownHeld && state !== undefined && targetable(state)) {
      return false;
    }
    this.current = NO_HANDLE;
    return true;
  }

  /**
   * Forgets the selection and the frames counted, as a session ends: a new session holds no target.
   */
  public reset(): void {
    this.current = NO_HANDLE;
    this.sentAt.fill(Number.NEGATIVE_INFINITY);
    this.sentNext = 0;
  }
}
